/**
 * Keyboard model for a combobox with a listbox of suggestions (WAI-ARIA
 * "Combobox with List Autocomplete, Manual Selection"): the pure part, so the
 * address search can be exercised without a DOM.
 *
 * `highlighted` is the index of the active option, or -1 when nothing is
 * highlighted (the input itself is "active"). Arrow keys wrap around.
 */
export type ListboxState = { open: boolean; highlighted: number };

export type ListboxAction =
  | { type: "move"; by: 1 | -1 }
  | { type: "close" }
  | { type: "open" }
  | { type: "results"; count: number }
  | { type: "hover"; index: number };

export function listboxReducer(
  state: ListboxState,
  action: ListboxAction,
  count: number,
): ListboxState {
  switch (action.type) {
    case "move": {
      if (count === 0) return { open: false, highlighted: -1 };
      if (!state.open || state.highlighted < 0) {
        // Opening (or re-entering) the list: Down lands on the first option,
        // Up on the last — the same convention as native <select> and the
        // ARIA practices example.
        return { open: true, highlighted: action.by > 0 ? 0 : count - 1 };
      }
      return { open: true, highlighted: (state.highlighted + action.by + count) % count };
    }
    case "close":
      return { open: false, highlighted: -1 };
    case "open":
      return { open: count > 0, highlighted: -1 };
    case "results":
      // New suggestions replace the old highlight: it pointed at text that is
      // no longer there.
      return { open: action.count > 0, highlighted: -1 };
    case "hover":
      return { open: true, highlighted: action.index };
  }
}

export type ListboxKey = "ArrowDown" | "ArrowUp" | "Enter" | "Escape" | "Tab";

/**
 * What a key press on the combobox input should do.
 *
 * - `select`   — commit the highlighted option (Enter with an active option)
 * - `submit`   — Enter with nothing highlighted: let the form submit
 * - `move`/`close` — update the listbox state
 * - null       — not ours; leave the default behaviour alone
 */
export function interpretComboboxKey(
  key: string,
  state: ListboxState,
  count: number,
):
  | { kind: "move"; by: 1 | -1 }
  | { kind: "select"; index: number }
  | { kind: "submit" }
  | { kind: "close"; preventDefault: boolean }
  | null {
  switch (key) {
    case "ArrowDown":
      return { kind: "move", by: 1 };
    case "ArrowUp":
      return { kind: "move", by: -1 };
    case "Enter":
      if (state.open && state.highlighted >= 0 && state.highlighted < count) {
        return { kind: "select", index: state.highlighted };
      }
      return { kind: "submit" };
    case "Escape":
      // Only swallow Escape while the list is showing; otherwise a dialog or
      // the browser may legitimately want it.
      return state.open ? { kind: "close", preventDefault: true } : null;
    case "Tab":
      return state.open ? { kind: "close", preventDefault: false } : null;
    default:
      return null;
  }
}
